/**
 * useConvexXP Hook
 *
 * Provides XP (experience points) data using Convex real-time subscriptions.
 * Replaces the REST API-based XPContext for the sidebar XP display.
 *
 * Features:
 * - Real-time XP and level updates via Convex subscription
 * - Streak data for daily engagement tracking
 * - XP history with pagination
 * - Automatic refresh on XP award events
 */

import { useQuery } from 'convex/react';
import { api } from '@careercard/convex/convex/_generated/api';
import { useMemo, useCallback, useEffect, useRef } from 'react';
import { xpEvents } from '../../utils/xpEvents';
import { createLogger } from '../../utils/logger-ssr-safe';

const logger = createLogger({ module: 'useConvexXP', level: 'info' });

// =============================================================================
// Types
// =============================================================================

/**
 * XP progress data compatible with existing components
 */
export interface ConvexXPProgress {
  xp: {
    total_xp: number;
    current_level: string;
    xp_for_current_level: number;
    xp_for_next_level: number;
    progress_percentage: number;
    daily_xp: Record<string, number>;
    weekly_actions: Record<string, number>;
    monthly_actions: Record<string, number>;
  };
  streak: {
    current_streak: number;
    longest_streak: number;
  };
}

/**
 * XP history entry type
 */
export interface ConvexXPHistoryEntry {
  id: string;
  action_type: string;
  xp_earned: number;
  metadata: Record<string, unknown>;
  created_at: string;
}

/**
 * Return type for the hook
 */
export interface UseConvexXPReturn {
  /** XP progress data */
  progress: ConvexXPProgress | null;
  /** Whether data is loading */
  loading: boolean;
  /** Error if any */
  error: Error | null;
  /** Refresh XP data (no-op for Convex, kept for compatibility) */
  refreshXP: () => Promise<void>;
}

// =============================================================================
// Level name mapping
// =============================================================================

import type { UserLevel } from '../../types/xp';

/**
 * Map numeric level to UserLevel name for LevelIcon component.
 *
 * The Convex gamification system uses a formula-based level: level = √(XP / 100)
 * This maps to 8 named levels that match the LevelIcon component icons:
 * - explorer: 🌱 (level 0, 0-99 XP)
 * - novice: ⭐ (level 1, 100-399 XP)
 * - developer: 💫 (level 2-3, 400-1599 XP)
 * - strategist: 🌟 (level 4-5, 1600-3599 XP)
 * - master: 🏆 (level 6-7, 3600-6399 XP)
 * - virtuoso: 👑 (level 8-9, 6400-9999 XP)
 * - sage: 🔮 (level 10-12, 10000-16899 XP)
 * - legend: ⚡ (level 13+, 16900+ XP)
 */
function getLevelName(level: number): UserLevel {
  if (level === 0) return 'explorer';
  if (level === 1) return 'novice';
  if (level <= 3) return 'developer';
  if (level <= 5) return 'strategist';
  if (level <= 7) return 'master';
  if (level <= 9) return 'virtuoso';
  if (level <= 12) return 'sage';
  return 'legend';
}

// =============================================================================
// Hook Implementation
// =============================================================================

/**
 * Hook for XP data via Convex real-time subscriptions.
 *
 * @returns XP progress data and loading state
 *
 * @example
 * ```tsx
 * const { progress, loading, refreshXP } = useConvexXP();
 *
 * if (loading && !progress) return <Spinner />;
 *
 * return (
 *   <div>
 *     <LevelIcon level={progress?.xp.current_level} />
 *     <span>{progress?.xp.total_xp} XP</span>
 *   </div>
 * );
 * ```
 */
export function useConvexXP(): UseConvexXPReturn {
  const isMountedRef = useRef(true);

  // Query XP data from Convex
  const xpData = useQuery(api.gamification.getXp);
  const streakData = useQuery(api.gamification.getStreak);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Listen for XP award events (for components that dispatch them)
  useEffect(() => {
    const unsubscribe = xpEvents.onXPAwarded(() => {
      if (isMountedRef.current) {
        logger.debug('XP awarded event received - Convex will auto-refresh');
        // No manual refresh needed - Convex subscriptions auto-update
      }
    });

    return unsubscribe;
  }, []);

  // Transform Convex data to the expected progress format
  const progress = useMemo<ConvexXPProgress | null>(() => {
    // Wait for both queries to load
    if (xpData === undefined || streakData === undefined) {
      return null;
    }

    const levelName = getLevelName(xpData.currentLevel);
    const progressPercentage = xpData.xpForNextLevel > xpData.xpForCurrentLevel
      ? ((xpData.totalXp - xpData.xpForCurrentLevel) / (xpData.xpForNextLevel - xpData.xpForCurrentLevel)) * 100
      : 100;

    return {
      xp: {
        total_xp: xpData.totalXp,
        current_level: levelName,
        xp_for_current_level: xpData.xpForCurrentLevel,
        xp_for_next_level: xpData.xpForNextLevel,
        progress_percentage: Math.min(100, Math.max(0, progressPercentage)),
        // These are placeholders - the XPModal fetches detailed stats separately
        daily_xp: {},
        weekly_actions: {},
        monthly_actions: {},
      },
      streak: {
        current_streak: streakData.currentStreak,
        longest_streak: streakData.longestStreak,
      },
    };
  }, [xpData, streakData]);

  // Refresh is a no-op for Convex (data is real-time)
  const refreshXP = useCallback(async () => {
    // Convex queries are reactive, no manual refresh needed
    logger.debug('refreshXP called - Convex handles updates automatically');
  }, []);

  const loading = xpData === undefined || streakData === undefined;

  return {
    progress,
    loading,
    error: null, // Convex handles errors internally
    refreshXP,
  };
}

export default useConvexXP;
